import { DefaultLanguage } from '../i18n/config';

export interface FormatCurrencyOptions extends Omit<
  Intl.NumberFormatOptions,
  'style' | 'currency'
> {
  /**
   * Locale (`en-US`, `es-MX`, `fr-CA`). When omitted, falls back to the
   * configured default.
   */
  locale?: string;
}

/**
 * Default currency when none is supplied. Per
 * [architecture §5.9.4](../../../../docs/shared/architecture.md), the SPA
 * falls back to USD so a misconfigured `Customer.DefaultCurrency` still
 * renders a sensible value rather than throwing.
 */
export const DefaultCurrency = 'USD';

/**
 * Formats a monetary value in the supplied currency, with locale-specific
 * symbol and decimal conventions:
 *  - `en-US` + USD → `$125.50`
 *  - `en-US` + CAD → `CA$125.50`
 *  - `fr-CA` + CAD → `125,50 $` (currency suffix, comma decimal, NBSP)
 *  - `es-MX` + MXN → `$125.50` (the `$` prefix is the MXN convention in Mexico)
 *
 * Falls back to `DefaultCurrency` (USD) when `currencyCode` is empty so
 * misconfigured customer records still render. Module code should pass the
 * customer-or-user currency explicitly when known.
 */
export function formatCurrency(
  value: number,
  currencyCode?: string | null,
  opts: FormatCurrencyOptions = {},
): string {
  const { locale, ...intlOpts } = opts;
  const currency = currencyCode && currencyCode.trim().length > 0 ? currencyCode : DefaultCurrency;

  return new Intl.NumberFormat(locale ?? DefaultLanguage, {
    style: 'currency',
    currency,
    ...intlOpts,
  }).format(value);
}
